import { Check, X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FlexProps, UIProps } from '../../../../theme/mixins';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

export interface StatusLabelsProps {
  amount: number;
  remainingAmount: number;
  refundedAmount: number;
  balance: number;
  cancelled: boolean;
  waitlist: boolean;
  sc?: FlexProps['flex'] & UIProps;
}

const StatusLabels = ({
  amount, remainingAmount, refundedAmount, balance, cancelled, waitlist, sc,
}: StatusLabelsProps) => {
  const isPending = amount > 0 && amount === remainingAmount;
  const isFullyRefunded = amount === 0 && refundedAmount > 0;
  const isFullyCancelled = cancelled && amount + remainingAmount + refundedAmount + balance === 0 && !waitlist;

  const hasRemainingAmount = remainingAmount > 0;
  const hasCreditedAmount = balance > 0;
  const hasRefundedAmount = refundedAmount > 0;

  return (
    <UI.FlexContainer sc={{ wrap: 'wrap', mx: -0.5, small: true, ...sc }}>
      {isFullyCancelled && (
        <UI.Div sc={{ padding: 0.5 }}>
          <CancelledLabel />
        </UI.Div>
      )}

      {!isFullyCancelled && (
        <>
          {(!hasRemainingAmount && !hasCreditedAmount && !isFullyRefunded && !waitlist) && (
            <UI.Div sc={{ padding: 0.5 }}>
              <PaidLabel />
            </UI.Div>
          )}

          {hasCreditedAmount && (
            <UI.Div sc={{ padding: 0.5 }}>
              <CreditedAmountLabel value={balance} />
            </UI.Div>
          )}

          {isPending && (
            <UI.Div sc={{ padding: 0.5 }}>
              <PendingLabel />
            </UI.Div>
          )}

          {!isPending && hasRemainingAmount && (
            <UI.Div sc={{ padding: 0.5 }}>
              <RemainingAmountLabel value={remainingAmount} />
            </UI.Div>
          )}

          {hasRefundedAmount && (
            <UI.Div sc={{ padding: 0.5 }}>
              <RefundedLabel value={refundedAmount} />
            </UI.Div>
          )}

          {waitlist && (
            <UI.Div sc={{ padding: 0.5 }}>
              <WaitlistLabel />
            </UI.Div>
          )}
        </>
      )}
    </UI.FlexContainer>
  );
};

const LabelContainer = styled(UI.Div)`
  ${UI.Label} {
    display: block;
    padding: 1px 5px;
    font-size: 0.75rem;
    white-space: nowrap;
  }
`;

const PaidLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'primary.50', color: 'primary.900', fontWeight: 500, noWrap: true }}>
        <UI.Icon>
          <Check />
        </UI.Icon>
        {' '}
        {t('paid')}
      </UI.Label>
    </LabelContainer>
  );
};

const CancelledLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'error.100', color: 'error.700', fontWeight: 500, noWrap: true }}>
        <UI.Icon>
          <X />
        </UI.Icon>
        {' '}
        {t('cancelled')}
      </UI.Label>
    </LabelContainer>
  );
};

export const PendingLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700', fontWeight: 500 }}>
        {t('pending')}
      </UI.Label>
    </LabelContainer>
  );
};

export const WaitlistLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ color: 'gray.800', fontWeight: 500 }}>
        {t('waitlist')}
      </UI.Label>
    </LabelContainer>
  );
};

const CreditedAmountLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'primary.100', color: 'primary.900', fontWeight: 500 }}>
        {t('n_amount_credit', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

const RemainingAmountLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700', fontWeight: 500 }}>
        {t('n_amount_remaining', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

const RefundedLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'secondary.100', color: 'secondary.700', fontWeight: 500 }}>
        {t('n_amount_refunded', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

export default StatusLabels;

